import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
    Search, ChevronDown, ArrowRight, Lightbulb, Crown, LifeBuoy, BookOpen, X,
    Rocket, Plus, LayoutDashboard, Receipt, Repeat, RefreshCw, Gauge, Target, CandlestickChart,
    ArrowLeftRight, CalendarDays, BarChart3, Calculator, Settings as SettingsIcon, Bot, Sparkles,
    PiggyBank, PieChart, ShieldCheck, Users,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/AuthContext";
import { useHousehold } from "@/contexts/HouseholdContext";
import { hasProAccess } from "@/lib/plans";
import { SECTIONS, ARTICLES } from "@/lib/helpContent";

// "Help & tips" — friendly in-app guide. Content lives in lib/helpContent.js; this file is layout only.
// Articles can be deep-linked as /help#<article-id> (opens + scrolls to it).
const ICONS = {
    rocket: Rocket, plus: Plus, dashboard: LayoutDashboard, receipt: Receipt, repeat: Repeat, refresh: RefreshCw,
    gauge: Gauge, target: Target, candles: CandlestickChart, arrows: ArrowLeftRight, calendar: CalendarDays,
    chart: BarChart3, calculator: Calculator, settings: SettingsIcon, bot: Bot, sparkles: Sparkles, piggy: PiggyBank,
    pie: PieChart, shield: ShieldCheck, crown: Crown, users: Users,
};

const searchable = (a) => [a.title, a.summary, a.example, a.tip, ...(a.points || [])].filter(Boolean).join(" ").toLowerCase();

export default function Help() {
    const { user } = useAuth();
    const { isMember } = useHousehold();
    const hasPro = hasProAccess(user, isMember);
    const location = useLocation();
    const [query, setQuery] = useState("");
    const [open, setOpen] = useState(() => new Set(["first-week"]));

    const q = query.trim().toLowerCase();
    const results = useMemo(
        () => (q ? ARTICLES.filter(a => q.split(/\s+/).every(w => searchable(a).includes(w))) : ARTICLES),
        [q],
    );

    // Deep link: /help#sinking-funds opens that article and scrolls to it.
    useEffect(() => {
        const id = decodeURIComponent(location.hash.replace("#", ""));
        if (!id || !ARTICLES.some(a => a.id === id)) return;
        setOpen(prev => new Set(prev).add(id));
        setTimeout(() => document.getElementById(`help-${id}`)?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
    }, [location.hash]);

    const toggle = (id) => setOpen(prev => {
        const next = new Set(prev);
        next.has(id) ? next.delete(id) : next.add(id);
        return next;
    });

    const jumpTo = (sectionId) => {
        setQuery("");
        setTimeout(() => document.getElementById(`section-${sectionId}`)?.scrollIntoView({ behavior: "smooth", block: "start" }), 0);
    };

    return (
        <div className="space-y-5 max-w-3xl" data-testid="help-page">
            <div>
                <h1 className="font-display text-2xl font-extrabold tracking-tight text-slate-900 flex items-center gap-2">
                    <BookOpen className="w-6 h-6 text-indigo-600" /> Help &amp; tips
                </h1>
                <p className="text-sm text-slate-500 mt-1">Short guides to every part of BudgetWise, plus a few money habits that make it work.</p>
            </div>

            <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                    value={query} onChange={e => setQuery(e.target.value)} placeholder="Search help — e.g. sinking fund"
                    className="h-11 rounded-xl pl-9 pr-9" data-testid="help-search" aria-label="Search help"
                />
                {query && (
                    <button type="button" onClick={() => setQuery("")} aria-label="Clear search"
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                        <X className="w-4 h-4" />
                    </button>
                )}
            </div>

            {!q && (
                <div className="flex gap-2 overflow-x-auto no-scrollbar -mx-1 px-1" data-testid="help-sections">
                    {SECTIONS.map(s => (
                        <button key={s.id} type="button" onClick={() => jumpTo(s.id)}
                            className="shrink-0 px-3 py-1.5 rounded-full bg-slate-100 hover:bg-slate-200 text-xs font-semibold text-slate-700 transition-colors">
                            {s.label}
                        </button>
                    ))}
                </div>
            )}

            {q ? (
                results.length ? (
                    <div className="space-y-2" data-testid="help-results">
                        <p className="text-xs text-slate-500">{results.length} result{results.length === 1 ? "" : "s"}</p>
                        {results.map(a => <ArticleCard key={a.id} a={a} isOpen hasPro={hasPro} onToggle={() => {}} />)}
                    </div>
                ) : (
                    <div className="card-elev rounded-2xl p-8 text-center text-sm text-slate-500" data-testid="help-no-results">
                        Nothing found for "{query}". Try a simpler word, or{" "}
                        <Link to="/contact" className="font-semibold text-indigo-600">ask us</Link>.
                    </div>
                )
            ) : (
                SECTIONS.map(s => (
                    <section key={s.id} id={`section-${s.id}`} className="space-y-2 scroll-mt-20">
                        <h2 className="text-xs font-bold uppercase tracking-wider text-slate-500 pt-2">{s.label}</h2>
                        {ARTICLES.filter(a => a.section === s.id).map(a => (
                            <ArticleCard key={a.id} a={a} isOpen={open.has(a.id)} hasPro={hasPro} onToggle={() => toggle(a.id)} />
                        ))}
                    </section>
                ))
            )}

            <div className="card-elev rounded-2xl p-5 flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                    <span className="w-10 h-10 rounded-xl grid place-items-center bg-cyan-50 text-cyan-600"><LifeBuoy className="w-5 h-5" /></span>
                    <div>
                        <div className="text-sm font-semibold text-slate-900">Still stuck?</div>
                        <div className="text-xs text-slate-500">We're happy to help — send us a message.</div>
                    </div>
                </div>
                <Link to="/contact" className="shrink-0 px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold">
                    Contact us
                </Link>
            </div>
        </div>
    );
}

function ArticleCard({ a, isOpen, onToggle, hasPro }) {
    const Icon = ICONS[a.icon] || BookOpen;
    const locked = a.pro && !hasPro;
    return (
        <div id={`help-${a.id}`} className="card-elev rounded-2xl overflow-hidden scroll-mt-20" data-testid={`help-article-${a.id}`}>
            <button type="button" onClick={onToggle} aria-expanded={isOpen}
                className="w-full flex items-center gap-3 p-4 text-left hover:bg-slate-50/60 transition-colors">
                <span className="w-9 h-9 rounded-xl grid place-items-center shrink-0" style={{ backgroundColor: `${a.color}18`, color: a.color }}>
                    <Icon className="w-[18px] h-[18px]" />
                </span>
                <span className="flex-1 min-w-0">
                    <span className="flex items-center gap-2">
                        <span className="font-semibold text-sm text-slate-900">{a.title}</span>
                        {a.pro && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-amber-50 text-amber-700 text-[10px] font-bold">
                                <Crown className="w-3 h-3" /> Pro
                            </span>
                        )}
                    </span>
                    <span className="block text-xs text-slate-500 mt-0.5">{a.summary}</span>
                </span>
                <ChevronDown className={`w-4 h-4 text-slate-400 shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`} />
            </button>
            {isOpen && (
                <div className="px-4 pb-4 pl-16 space-y-3 text-sm text-slate-600">
                    {a.points?.length > 0 && (
                        <ul className="space-y-1.5 list-disc pl-4 marker:text-slate-300">
                            {a.points.map((p, i) => <li key={i}>{p}</li>)}
                        </ul>
                    )}
                    {a.example && (
                        <div className="rounded-xl bg-slate-50 px-3 py-2 text-xs text-slate-600">
                            <span className="font-semibold text-slate-800">Example: </span>{a.example}
                        </div>
                    )}
                    {a.tip && (
                        <div className="flex gap-2 rounded-xl bg-amber-50/70 px-3 py-2 text-xs text-amber-900">
                            <Lightbulb className="w-4 h-4 shrink-0 text-amber-500" /><span>{a.tip}</span>
                        </div>
                    )}
                    {(a.link || locked) && (
                        <div className="flex flex-wrap gap-2 pt-1">
                            {a.link && (
                                <Link to={a.link.to} className="inline-flex items-center gap-1 text-xs font-semibold text-indigo-600 hover:text-indigo-700">
                                    {a.link.label} <ArrowRight className="w-3.5 h-3.5" />
                                </Link>
                            )}
                            {locked && (
                                <Link to="/pricing" className="inline-flex items-center gap-1 text-xs font-semibold text-amber-600 hover:text-amber-700">
                                    <Crown className="w-3.5 h-3.5" /> Included in Pro &amp; Family
                                </Link>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
